import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input, Label, Textarea } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2, Star, Eye, EyeOff, ExternalLink, Copy, ImageOff, Leaf } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  title: "", category: "Landscape", client: "", location: "Singapore",
  year_completed: new Date().getFullYear(), description: "",
  image_url: "", tags_csv: "", esg_highlights_json: "",
  is_public: true, featured: false, source_url: "",
};

const CATEGORY_OPTIONS = [
  "Landscape", "Water Feature", "Sculpture", "Rockworks",
  "Stamp Concrete", "Facade", "Specialty Build", "Maintenance",
  "Renewable Energy", "Building Retrofit", "Other",
];

function ProjectCard({ p, onEdit, onDelete, onToggle }) {
  return (
    <div className="card-flat overflow-hidden flex flex-col group" data-testid={`gallery-card-${p.id}`}>
      <div className="aspect-[4/3] bg-surface relative overflow-hidden">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={p.title}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
          />
        ) : null}
        <div className="absolute inset-0 hidden items-center justify-center text-muted-foreground bg-surface">
          <ImageOff className="h-6 w-6" />
        </div>
        <div className="absolute top-2 left-2 flex gap-1.5">
          {p.featured && <Badge variant="lime"><Star className="h-3 w-3 mr-1 fill-current" /> Featured</Badge>}
          {!p.is_public && <Badge variant="warning"><EyeOff className="h-3 w-3 mr-1" /> Private</Badge>}
        </div>
        <div className="absolute top-2 right-2"><Badge variant="outline" className="bg-background/80 backdrop-blur">{p.category}</Badge></div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-display font-semibold tracking-tight leading-tight mb-1" data-testid={`gallery-title-${p.id}`}>{p.title}</div>
        <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-2">
          {p.client || "—"}{p.year_completed ? ` · ${p.year_completed}` : ""}{p.location ? ` · ${p.location}` : ""}
        </div>
        {p.description && <p className="text-xs text-muted-foreground line-clamp-3 mb-3">{p.description}</p>}
        {p.esg_highlights && Object.keys(p.esg_highlights).length > 0 && (
          <div className="flex items-start gap-1.5 text-[10px] font-mono text-emerald-400 mb-3">
            <Leaf className="h-3 w-3 mt-0.5 shrink-0" />
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              {Object.entries(p.esg_highlights).slice(0, 3).map(([k, v]) => (
                <span key={k}>{k.replace(/_/g, " ")}: <span className="text-foreground">{String(v)}</span></span>
              ))}
            </div>
          </div>
        )}
        {p.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-auto pb-2">
            {p.tags.slice(0, 4).map((t) => <Badge key={t} variant="outline" className="text-[9px]">{t}</Badge>)}
          </div>
        )}
        <div className="flex gap-1 pt-2 border-t border-border mt-2">
          <Button variant="ghost" size="sm" className="text-xs flex-1" onClick={() => onEdit(p)} data-testid={`edit-${p.id}`}>
            <Pencil className="h-3 w-3" /> Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onToggle(p)} title={p.is_public ? "Hide from public" : "Show on public"} data-testid={`toggle-${p.id}`}>
            {p.is_public ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(p)} data-testid={`delete-${p.id}`}>
            <Trash2 className="h-3.5 w-3.5 text-red-400" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectGallery() {
  const { tenant } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery");
      setProjects(data.projects);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const s = new Set(projects.map(p => p.category).filter(Boolean));
    return ["ALL", ...Array.from(s).sort()];
  }, [projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      if (filter !== "ALL" && p.category !== filter) return false;
      if (!q) return true;
      return (p.title + " " + (p.client || "") + " " + (p.description || "") + " " + (p.tags || []).join(" "))
        .toLowerCase().includes(q);
    });
  }, [projects, filter, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "",
      category: p.category || "Landscape",
      client: p.client || "",
      location: p.location || "",
      year_completed: p.year_completed || new Date().getFullYear(),
      description: p.description || "",
      image_url: p.image_url || "",
      tags_csv: (p.tags || []).join(", "),
      esg_highlights_json: JSON.stringify(p.esg_highlights || {}, null, 2),
      is_public: p.is_public ?? true,
      featured: p.featured ?? false,
      source_url: p.source_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.image_url) {
      toast.error("Title and image URL are required");
      return;
    }
    let highlights = {};
    if (form.esg_highlights_json.trim()) {
      try { highlights = JSON.parse(form.esg_highlights_json); }
      catch { toast.error("ESG highlights must be valid JSON"); return; }
    }
    const payload = {
      title: form.title,
      category: form.category,
      client: form.client || null,
      location: form.location || null,
      year_completed: form.year_completed ? parseInt(form.year_completed) : null,
      description: form.description || "",
      image_url: form.image_url,
      additional_images: [],
      tags: form.tags_csv.split(",").map(t => t.trim()).filter(Boolean),
      esg_highlights: highlights,
      is_public: !!form.is_public,
      featured: !!form.featured,
      source_url: form.source_url || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/gallery/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/gallery", payload);
        toast.success("Project added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    await api.delete(`/gallery/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (p) => {
    await api.put(`/gallery/${p.id}`, { is_public: !p.is_public });
    load();
  };

  const publicUrl = `${window.location.origin}/gallery/public/${tenant?.slug || ""}`;
  const copyPublicLink = () => {
    navigator.clipboard?.writeText(publicUrl);
    toast.success("Public gallery link copied");
  };

  const counts = {
    total: projects.length,
    public: projects.filter(p => p.is_public).length,
    featured: projects.filter(p => p.featured).length,
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="project-gallery-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Module · Showcase</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Project Gallery</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Curate a visual portfolio of your green / ESG-relevant work.
            Toggle individual projects public — share one link with prospects and clients.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div className="card-flat p-3 text-center">
            <div className="label-eyebrow mb-1">Projects</div>
            <div className="font-display text-2xl font-bold" data-testid="gallery-count-total">{counts.total}</div>
          </div>
          <div className="card-flat p-3 text-center">
            <div className="label-eyebrow mb-1">Public</div>
            <div className="font-display text-2xl font-bold text-emerald-400" data-testid="gallery-count-public">{counts.public}</div>
          </div>
          <div className="card-flat p-3 text-center">
            <div className="label-eyebrow mb-1">Featured</div>
            <div className="font-display text-2xl font-bold text-accent">{counts.featured}</div>
          </div>
        </div>
      </div>

      {tenant?.slug && (
        <div className="card-flat p-4 flex items-center gap-3 flex-wrap" data-testid="public-link-bar">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow mb-1">Public showcase link</div>
            <div className="font-mono text-xs text-muted-foreground truncate">{publicUrl}</div>
          </div>
          <Button variant="outline" size="sm" onClick={copyPublicLink} data-testid="copy-public-link-btn">
            <Copy className="h-3.5 w-3.5" /> Copy
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button variant="primary" size="sm" data-testid="open-public-link-btn">
              <ExternalLink className="h-3.5 w-3.5" /> Open
            </Button>
          </a>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3 py-1 rounded-sm text-xs font-medium border transition-colors ${
                filter === c
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border text-muted-foreground hover:text-foreground hover:bg-surface-hover"
              }`}
            >
              {c}{c !== "ALL" && <span className="ml-1 opacity-50">{projects.filter(p => p.category === c).length}</span>}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-48"
            data-testid="gallery-search"
          />
          <Button variant="primary" size="sm" onClick={openCreate} data-testid="new-project-btn">
            <Plus className="h-4 w-4" /> Add project
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground font-mono text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-16 text-center text-muted-foreground">
          <div className="font-display text-lg mb-2">No projects yet</div>
          <p className="text-sm">Click &ldquo;Add project&rdquo; to start showcasing your sustainable work.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="gallery-grid">
          {filtered.map((p) => (
            <ProjectCard key={p.id} p={p} onEdit={openEdit} onDelete={remove} onToggle={toggle} />
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit project" : "Add project"}</DialogTitle>
            <DialogDescription>Showcase your sustainable / ESG-relevant work. Anything you mark public appears on your shareable gallery link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="form-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Year completed</Label><Input type="number" value={form.year_completed} onChange={(e) => setForm({...form, year_completed: e.target.value})} /></div>
              <div><Label>Client</Label><Input value={form.client} onChange={(e) => setForm({...form, client: e.target.value})} data-testid="form-client" /></div>
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} /></div>
            </div>
            <div><Label>Image URL *</Label><Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="https://…" data-testid="form-image" /></div>
            {form.image_url && (
              <div className="card-flat overflow-hidden">
                <img src={form.image_url} alt="preview" className="w-full max-h-52 object-cover" referrerPolicy="no-referrer" />
              </div>
            )}
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} /></div>
            <div><Label>Tags (comma-separated)</Label><Input value={form.tags_csv} onChange={(e) => setForm({...form, tags_csv: e.target.value})} placeholder="Native Planting, Water Saving" /></div>
            <div>
              <Label>ESG highlights (JSON — optional)</Label>
              <Textarea rows={3} value={form.esg_highlights_json} onChange={(e) => setForm({...form, esg_highlights_json: e.target.value})} placeholder='{"native_plants": 320, "water_saved_m3_yr": 45}' className="font-mono text-xs" />
              <div className="text-[10px] text-muted-foreground mt-1">Numeric values aggregate into the public showcase hero.</div>
            </div>
            <div><Label>Source URL (optional)</Label><Input value={form.source_url} onChange={(e) => setForm({...form, source_url: e.target.value})} placeholder="https://…" /></div>
            <div className="flex gap-4 pt-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_public} onChange={(e) => setForm({...form, is_public: e.target.checked})} data-testid="form-public" />
                Public (visible on share link)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="form-featured" />
                Featured (top of gallery)
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving} data-testid="form-save">
              {saving ? "Saving…" : editing ? "Save" : "Add project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
